import React, { useEffect, useMemo, useState } from "react";
import s from "./myPageConsultation.module.scss";
import { ProfileApi } from "../../../../api/ProfileApi";
import { ConsultationApi } from "../../../../api/ConsultationApi";
import { HospitalReviewApi } from "../../../../api/HospitalReviewApi";
import { executePromise } from "../../../../util/sliceUtil";
import { UserProfile } from "../../../../types/user";
import { ConsultationItem } from "../../../../types/consultation";
import { PageResponse } from "../../../../types/page";
import { useModal } from "../../../../hooks/hooks";

// kpass_mypage_consultation.html 시안의 React 이식본 (전부 실데이터 — 프론트 더미 없음).
// 프로필/검사정보/신청내역/이용권/추천상담사/상담사리뷰 → 백엔드 API
// 병원 검색 → 네이버 지역검색(백엔드 프록시 /user/hospital/search), 병원 후기/평점 → 자체 후기 DB
// 미구현(정적 안내): 병원 AI 후기요약(개발 예정)

// 병원 검색 분류 필터 라벨
const HOSPITAL_FILTERS = [
  { key: "정신건강의학과", label: "정신건강의학과" },
  { key: "소아청소년정신과", label: "소아청소년정신과" },
  { key: "심리발달센터", label: "심리발달센터" },
  { key: "인지심리발달센터", label: "인지심리발달센터" },
  { key: "아동발달센터", label: "아동발달센터" },
  { key: "심리상담센터", label: "심리상담센터" },
];

type Tab = "consult" | "history";
type Route = "a" | "b" | "c";

const BRAIN_TYPE_LABEL: { [k: string]: string } = {
  SIMULTANEOUS: "우뇌우세형",
  SUCCESSIVE: "좌뇌우세형",
  BALANCED: "좌우밸런스형",
};

const STATUS_LABEL: { [k: string]: string } = {
  NOT_ALLOTTED: "상담사 미배정",
  ALLOTTED: "상담사 배정완료",
  FINISHED: "상담종료",
  REFUNDED: "환불처리",
  CANCELED: "취소처리",
};

// 영역별 강점/지원필요 뱃지 (ConsultationItem의 *Status 필드)
const DOMAINS: { key: "successiveStatus" | "simultaneousStatus" | "planningStatus" | "attentionStatus"; label: string }[] = [
  { key: "successiveStatus", label: "순차처리" },
  { key: "simultaneousStatus", label: "동시처리" },
  { key: "planningStatus", label: "계획력" },
  { key: "attentionStatus", label: "주의력" },
];

const fmtDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");
// 예약 일시: "2026-07-10" + "10:00:00" → "2026년 7월 10일 10:00"
const fmtReserveDateTime = (date: string | null, time: string | null) => {
  if (!date) return "—";
  const [y, m, d] = date.split("-");
  const hm = time ? time.slice(0, 5) : "";
  return `${y}년 ${Number(m)}월 ${Number(d)}일${hm ? ` ${hm}` : ""}`;
};
// 리뷰 작성 일시: "2026-06-26T14:30:..." → "2026-06-26 14:30"
const fmtReviewDateTime = (iso: string | null) => (iso ? iso.slice(0, 16).replace("T", " ") : "");
const star = (n: number) => "★".repeat(Math.floor(n)) + "☆".repeat(5 - Math.floor(n));

interface NaverItem {
  title: string; link: string; category: string; telephone: string;
  address: string; roadAddress: string; mapx: string; mapy: string;
}

// 백엔드 추천 상담사 (UserConsultantListDTO). dev 배포 후 실데이터.
interface RealConsultant {
  idx: number;
  consultantName: string;
  briefHistory: string;
  affiliationName: string | null;
  image: { url?: string } | null;
  avgRating: number | null;
  reviewCount: number;
}

export function MyPageConsultation() {
  const [tab, setTab] = useState<Tab>("consult");
  const [route, setRoute] = useState<Route | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // 상담사 예약 모달 (희망 상담사 지정)
  const [reserveTarget, setReserveTarget] = useState<{ adminIdx: number; name: string } | null>(null);
  const [openReviewConsultant, setOpenReviewConsultant] = useState<number | null>(null); // 리뷰 펼친 상담사

  // ── 실데이터 상태 ──
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [profileIdx, setProfileIdx] = useState<number | null>(null);
  const [history, setHistory] = useState<ConsultationItem[]>([]);
  const [voucherCount, setVoucherCount] = useState<number>(0);
  const [realConsultants, setRealConsultants] = useState<RealConsultant[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 4000); };
  const { openConfirmModal, openAlertModal } = useModal();

  // 페이지 최초 진입 시에만 최상단으로 (route/검색 등 이후 동작에는 영향 없음)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 프로필 로드 → 소유 프로필 선택
  useEffect(() => {
    (async () => {
      try {
        // executePromise 결과 = 응답 데이터 자체(HttpClient 인터셉터가 .data 언랩)
        const list = ((await executePromise(ProfileApi.getProfiles())) as unknown as UserProfile[]) || [];
        setProfiles(list);
        const owner = list.find((p) => p.isOwnerProfile) || list[0];
        setProfileIdx(owner ? owner.idx : null);
      } catch (e: any) {
        setErr("프로필을 불러오지 못했습니다. (로그인 필요)");
      }
    })();
  }, []);

  // 추천 상담사 실데이터 로드 (백엔드 dev 배포 후 동작. 미배포면 더미 폴백)
  useEffect(() => {
    (async () => {
      try {
        const list = ((await executePromise(ConsultationApi.getRecommendConsultants())) as unknown as RealConsultant[]) || [];
        setRealConsultants(list);
      } catch {
        setRealConsultants([]); // 엔드포인트 미배포 → 더미 사용
      }
    })();
  }, []);

  // 선택 프로필의 상담/검사 내역 + 이용권 로드
  useEffect(() => {
    if (profileIdx == null) return;
    setLoading(true);
    (async () => {
      try {
        const [his, vou] = await Promise.all([
          executePromise(ConsultationApi.getConsultationHistory(profileIdx, { page: 0, size: 20 })),
          executePromise(ConsultationApi.getCounselingVoucher()),
        ]);
        setHistory(((his as unknown as PageResponse<ConsultationItem>)?.content) || []);
        setVoucherCount(((vou as unknown as { count: number })?.count) ?? 0);
        setErr(null);
      } catch (e: any) {
        setErr("상담 내역을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    })();
  }, [profileIdx]);

  const selectedProfile = useMemo(() => profiles.find((p) => p.idx === profileIdx) || null, [profiles, profileIdx]);
  // 검사 선택: 프로필의 검사 목록(history)에서 선택한 검사로 상담 신청 (기존 마이페이지처럼 검사마다)
  const [selectedExamIdx, setSelectedExamIdx] = useState<number | null>(null);
  useEffect(() => {
    // 프로필/내역 바뀌면 첫 검사로 초기화
    setSelectedExamIdx(history.length > 0 ? history[0].contentExecutedInfoIdx : null);
  }, [history]);
  const selectedTest = useMemo(
    () => history.find((h) => h.contentExecutedInfoIdx === selectedExamIdx) || history[0] || null,
    [history, selectedExamIdx],
  );
  // 선택 검사가 이미 신청됨(status 있음)이면 그 검사 상담 상태에 따라 분기
  const isExamApplied = !!selectedTest?.status;
  // 미배정(NOT_ALLOTTED): 상담사 배정 대기 중 (상담사 카드 없음, 신청내역에서 취소 가능)
  const isWaitingAllocation = selectedTest?.status === "NOT_ALLOTTED";
  // 배정/종료 등: 그 검사에 배정된 상담사
  const assignedConsultant = useMemo(
    () => (isExamApplied && selectedTest?.consultantAdminIdx != null
      ? realConsultants.find((c) => c.idx === selectedTest.consultantAdminIdx) || null
      : null),
    [isExamApplied, selectedTest, realConsultants],
  );

  // 예약 일시 박스 (몇월 며칠 몇시)
  const reserveDateTimeBox = selectedTest?.desiredDate ? (
    <div className={s.infoBox} style={{ background: "#EEEDFE", color: "#3C3489" }}>
      📅 예약 일시: {fmtReserveDateTime(selectedTest.desiredDate, selectedTest.desiredTime)}
    </div>
  ) : null;

  // 신청된 검사일 때 상담사 영역에 보여줄 내용
  const renderAppliedConsultant = () => {
    if (isWaitingAllocation) {
      return (
        <>
          {reserveDateTimeBox}
          <div className={s.infoBox}>상담 신청이 접수되었습니다. 상담사 배정을 기다리고 있어요.<br />신청 내역 탭에서 진행 상태를 확인하거나 취소할 수 있습니다.</div>
        </>
      );
    }
    if (assignedConsultant) {
      return (
        <>
          {reserveDateTimeBox}
          {renderRealConsultant(assignedConsultant, true)}
        </>
      );
    }
    return <div className={s.infoBox}>신청한 상담사 정보를 불러올 수 없습니다. (신청 내역 탭에서 확인)</div>;
  };

  // 상담사 예약 모달 열기 — 검사/이용권 선검증 (공통 모달 사용)
  const openReserve = (adminIdx: number, name: string) => {
    if (!selectedTest) {
      openAlertModal({ title: "상담 신청 안내", desc: "상담 신청 가능한 검사 내역이 없습니다." });
      return;
    }
    if (selectedTest.status) {
      openAlertModal({ title: "상담 신청 안내", desc: "이미 해당 검사에 진행 중인 상담이 있습니다.\n신청 내역을 확인해주세요." });
      return;
    }
    if (voucherCount <= 0) {
      openConfirmModal(
        { title: "상담 신청 안내", desc: "상담이용권이 부족합니다.\n상담이용권 구매 페이지로 이동하시겠습니까?" },
        () => window.open("/purchase-counselor", "_blank", "noopener,noreferrer"),
      );
      return;
    }
    setReserveTarget({ adminIdx, name });
  };

  const refreshHistoryAndVoucher = () => {
    if (profileIdx == null) return;
    executePromise(ConsultationApi.getConsultationHistory(profileIdx, { page: 0, size: 20 }))
      .then((r) => setHistory(((r as unknown as PageResponse<ConsultationItem>)?.content) || []));
    executePromise(ConsultationApi.getCounselingVoucher())
      .then((r) => setVoucherCount(((r as unknown as { count: number })?.count) ?? 0));
  };

  const onReserveDone = () => {
    setReserveTarget(null);
    showToast("✅ 상담 신청이 완료되었습니다.");
    refreshHistoryAndVoucher();
  };

  // 상담 취소 (미배정만 가능 — 백엔드에서도 가드)
  const onClickCancel = (item: ConsultationItem) => {
    if (profileIdx == null || item.reservationIdx == null) return;
    openConfirmModal(
      { title: "상담 취소 안내", desc: "상담을 정말 취소하시겠습니까?\n취소 시 상담이용권이 1매 반환됩니다." },
      async () => {
        try {
          await executePromise(ConsultationApi.cancelCounseling({
            counselingIdx: item.counselingIdx,
            userProfileIdx: profileIdx,
            counselingReservationIdx: item.reservationIdx as number,
          }));
          showToast("✅ 상담이 취소되었습니다.");
          refreshHistoryAndVoucher();
        } catch (e: any) {
          let msg = "상담 취소에 실패했습니다.";
          try { const j = JSON.parse(e?.message || "{}"); if (j?.errorCode?.msg) msg = j.errorCode.msg; } catch {}
          openAlertModal({ title: "상담 취소 안내", desc: msg });
        }
      },
    );
  };

  const userName = selectedProfile?.name || "—";

  // ── 추천 상담사 카드 (실데이터) ──
  // applied=true면 이 검사가 이미 그 상담사에게 신청 완료된 상태 → 예약 대신 상태 표시
  const renderRealConsultant = (c: RealConsultant, applied = false) => (
    <div className={s.counselorCard} key={c.idx}>
      <div className={s.counselorAvatar}>
        {c.image?.url ? <img src={c.image.url} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : "👤"}
      </div>
      <div className={s.counselorInfo}>
        <div className={s.counselorName}>{c.consultantName}</div>
        <div className={s.counselorSpec}>{c.briefHistory || "—"}</div>
        {c.affiliationName && <div className={s.counselorTags}><span className={s.ctag}>{c.affiliationName}</span></div>}
      </div>
      <div className={s.counselorRating}>
        <div className={s.starScore}>★ {c.avgRating != null ? c.avgRating.toFixed(1) : "-"}</div>
        <div className={s.starCnt}>리뷰 {c.reviewCount}개</div>
        <button type="button" className={s.bookBtn} onClick={() => setOpenReviewConsultant(openReviewConsultant === c.idx ? null : c.idx)}
          style={{ background: "#fff", color: "var(--pu)", border: "1px solid var(--pu)" }}>
          리뷰 {openReviewConsultant === c.idx ? "닫기" : "보기"}
        </button>
        {applied
          ? <span className={s.statusDone} style={{ display: "inline-block", marginTop: 6 }}>{STATUS_LABEL[selectedTest?.status || ""] || "신청완료"}</span>
          : <button type="button" className={s.bookBtn} onClick={() => openReserve(c.idx, c.consultantName)}>예약하기</button>}
      </div>
      {openReviewConsultant === c.idx && <ConsultantReviewList adminIdx={c.idx} />}
    </div>
  );

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <h1>마이페이지 — {userName}</h1>
        <p className={s.sub}>D-CAS 검사 결과 기반 상담 및 전문 기관 연결 서비스</p>
        <div className={s.tabs}>
          <button type="button" className={`${s.tab} ${tab === "consult" ? s.active : ""}`} onClick={() => setTab("consult")}>상담 / 기관 찾기</button>
          <button type="button" className={`${s.tab} ${tab === "history" ? s.active : ""}`} onClick={() => setTab("history")}>신청 내역</button>
          {/* API 설정 탭은 사용자 페이지에 미노출(개발/관리용). 키 연동은 추후 백엔드로 이전. */}
        </div>
      </div>

      <div className={s.container}>
        {err && <div className={s.dummyBanner}>⚠️ {err}</div>}

        {/* 검사정보 카드 — 실데이터(프로필/검사) + 영역뱃지는 준비중 */}
        <div className={s.examCard}>
          <div className={s.sectionHeader}>
            <span className={s.sectionTitle}>검사정보 ({userName})</span>
            <span style={{ fontSize: 12, color: "#5F5E5A" }}>상담 이용권 {voucherCount}매</span>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            {profiles.length > 1 && (
              <select className={s.modalSelect} style={{ maxWidth: 200, marginBottom: 0 }}
                value={profileIdx ?? ""} onChange={(e) => setProfileIdx(Number(e.target.value))}>
                {profiles.map((p) => <option key={p.idx} value={p.idx}>{p.name}{p.isOwnerProfile ? " (본인)" : ""}</option>)}
              </select>
            )}
            {/* 검사 선택 — 검사마다 상담 신청 */}
            {history.length > 0 && (
              <select className={s.modalSelect} style={{ maxWidth: 280, marginBottom: 0 }}
                value={selectedExamIdx ?? ""} onChange={(e) => setSelectedExamIdx(Number(e.target.value))}>
                {history.map((h) => (
                  <option key={h.contentExecutedInfoIdx} value={h.contentExecutedInfoIdx}>
                    {h.unitVoucherType === "KPASS" ? "K-PASS" : "D-CAS"} {h.testRound}회차 ({fmtDate(h.endedAt)})
                    {h.status ? ` · ${STATUS_LABEL[h.status] || ""}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          {loading ? (
            <div className={s.examRow}>불러오는 중…</div>
          ) : selectedTest ? (
            <div className={s.examRow}>
              <span className={s.examType}>{selectedTest.unitVoucherType === "KPASS" ? "K-PASS" : "D-CAS"} {selectedTest.testRound}회차</span>
              {selectedTest.brainType && BRAIN_TYPE_LABEL[selectedTest.brainType] && (
                <span className={s.brainChip}>{BRAIN_TYPE_LABEL[selectedTest.brainType]}</span>
              )}
              {/* 영역별 강점(NS)/지원필요(NW) 뱃지 — 백엔드 실데이터 */}
              {DOMAINS.map(({ key, label }) => {
                const st = selectedTest[key];
                if (st === "NS") return <span key={key} className={`${s.brainChip} ${s.ns}`}>{label} 강점</span>;
                if (st === "NW") return <span key={key} className={`${s.brainChip} ${s.nw}`}>{label} 지원필요</span>;
                return null;
              })}
              <span className={s.examDate}>{fmtDate(selectedTest.endedAt)}</span>
            </div>
          ) : (
            <div className={s.examRow}>완료된 검사 내역이 없습니다.</div>
          )}
        </div>

        {/* ── 탭1: 상담/기관 찾기 ── */}
        {tab === "consult" && (
          <div className={s.panel}>
            {/* 선택 검사가 이미 상담 신청됨이면 안내 */}
            {isExamApplied && (
              <div className={s.infoBox} style={{ background: "#FAEEDA", color: "#BA7517" }}>
                선택한 검사는 이미 {selectedTest?.consultantName ? `${selectedTest.consultantName} 상담사에게 ` : ""}상담 신청되었습니다 ({STATUS_LABEL[selectedTest?.status || ""] || selectedTest?.status}). 다른 검사를 선택하면 새로 신청할 수 있습니다.
              </div>
            )}
            <div className={s.routeSection}>
              <div className={s.sectionHeader}>
                <span className={s.sectionTitle}>어떤 도움이 필요하신가요?</span>
                <span className={s.sectionBadge}>검사 결과 연동</span>
              </div>
              <div className={s.routeCards}>
                <button type="button" className={`${s.routeCard} ${route === "a" ? s.active : ""}`} onClick={() => setRoute("a")}>
                  {route === "a" && <span className={s.check} />}
                  <div className={`${s.routeIcon} ${s.iconA}`}>🧑‍💬</div>
                  <h3>상담만 원해요</h3><p>전문 상담사와 1:1로 결과 해석 및 방향 상담</p>
                </button>
                <button type="button" className={`${s.routeCard} ${route === "b" ? s.active : ""}`} onClick={() => setRoute("b")}>
                  {route === "b" && <span className={s.check} />}
                  <div className={`${s.routeIcon} ${s.iconB}`}>🏥</div>
                  <h3>상담 + 병원 치료</h3><p>상담 후 전문 기관 연계까지 한 번에</p>
                </button>
                <button type="button" className={`${s.routeCard} ${route === "c" ? s.active : ""}`} onClick={() => setRoute("c")}>
                  {route === "c" && <span className={s.check} />}
                  <div className={`${s.routeIcon} ${s.iconC}`}>🔍</div>
                  <h3>병원 바로 찾기</h3><p>D-CAS 결과 기반 맞춤 전문 기관 즉시 검색</p>
                </button>
              </div>
            </div>

            {route === "a" && (
              <div className={s.panel}>
                <div className={s.sectionHeader}>
                  <span className={s.sectionTitle}>{isExamApplied ? "신청한 상담사" : "추천 상담사"}</span>
                  <span className={s.sectionBadge}>D-CAS 연동</span>
                </div>
                <div className={s.counselorList}>
                  {isExamApplied
                    ? renderAppliedConsultant()
                    : (realConsultants.length > 0
                        ? realConsultants.map((c) => renderRealConsultant(c))
                        : <div className={s.infoBox}>현재 신청 가능한 상담사가 없습니다.</div>)}
                </div>
              </div>
            )}

            {route === "b" && (
              <div className={s.panel}>
                <div className={s.sectionHeader}><span className={s.sectionTitle}>{isExamApplied ? "신청한 상담사" : "상담 후 전문 기관 연계"}</span></div>
                {!isExamApplied && <div className={s.infoBox}>💡 먼저 상담사와 이야기 나눈 후, 필요 시 아래 전문 기관을 함께 결정할 수 있습니다.</div>}
                <div className={s.counselorList}>
                  {isExamApplied
                    ? renderAppliedConsultant()
                    : (realConsultants.length > 0
                        ? realConsultants.map((c) => renderRealConsultant(c))
                        : <div className={s.infoBox}>현재 신청 가능한 상담사가 없습니다.</div>)}
                </div>
                <div className={s.sectionHeader} style={{ marginTop: 20 }}><span className={s.sectionTitle}>연계 추천 기관</span><span className={s.sectionBadge}>네이버 검색</span></div>
                <HospitalLiveList />
              </div>
            )}

            {route === "c" && (
              <div className={s.panel}>
                <HospitalLiveList showFilters />
              </div>
            )}
          </div>
        )}

        {/* ── 탭2: 신청 내역 (실데이터) ── */}
        {tab === "history" && (
          <div className={s.panel}>
            <div className={s.sectionHeader}><span className={s.sectionTitle}>상담내역 ({userName})</span></div>
            <div className={s.historyCard}>
              <table className={s.historyTable}>
                <thead><tr><th>검사종류</th><th>상담요청일</th><th>기관/상담사</th><th>상세보기</th><th>상태</th></tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5}>불러오는 중…</td></tr>
                  ) : history.length === 0 ? (
                    <tr><td colSpan={5}>검사/상담 내역이 없습니다.</td></tr>
                  ) : history.map((h) => (
                    <tr key={h.contentExecutedInfoIdx}>
                      <td>{h.unitVoucherType === "KPASS" ? "K-PASS" : "D-CAS"} {h.testRound}회차</td>
                      <td>{h.desiredDate || "—"}</td>
                      <td>{h.consultantName || "—"}</td>
                      <td>—</td>
                      <td>
                        {!h.status ? (
                          <button type="button" className={s.applyBtn} onClick={() => { setSelectedExamIdx(h.contentExecutedInfoIdx); setTab("consult"); setRoute("a"); }}>상담신청</button>
                        ) : (
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <span className={h.status === "FINISHED" ? s.statusDone : s.statusPending}>{STATUS_LABEL[h.status] || h.status}</span>
                            {/* 미배정만 취소 가능 (배정 후엔 불가) */}
                            {h.status === "NOT_ALLOTTED" && h.reservationIdx != null && (
                              <button type="button" className={s.applyBtn} style={{ background: "#fff", color: "#993C1D", border: "1px solid #993C1D" }}
                                onClick={() => onClickCancel(h)}>취소</button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* 상담사 예약 모달 (실제 상담신청 + 희망상담사 + 이용권 차감) */}
      {reserveTarget && selectedTest && profileIdx != null && (
        <ReservationModal
          target={reserveTarget}
          profileIdx={profileIdx}
          contentExecutedInfoIdx={selectedTest.contentExecutedInfoIdx}
          testLabel={`${selectedTest.unitVoucherType === "KPASS" ? "K-PASS" : "D-CAS"} ${selectedTest.testRound}회차`}
          onClose={() => setReserveTarget(null)}
          onDone={onReserveDone}
          notify={(desc: string) => openAlertModal({ title: "상담 신청 안내", desc })}
        />
      )}

      {toast && <div className={s.toast}>{toast}</div>}
    </div>
  );
}

// ── 병원 라이브 검색 (네이버 지역검색, dev 프록시) ──
interface HospReview { idx: number; userName: string; rating: number; content: string; createAt: string | null }
interface HospSummary { avgRating: number | null; reviewCount: number }

function HospitalLiveList({ showFilters }: { showFilters?: boolean }) {
  const [query, setQuery] = useState("소아정신과");
  const [items, setItems] = useState<NaverItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState<string | null>(null); // placeKey
  const [summaries, setSummaries] = useState<Record<string, HospSummary>>({});
  const [reviews, setReviews] = useState<Record<string, HospReview[]>>({});
  const [form, setForm] = useState<{ rating: number; content: string }>({ rating: 5, content: "" });
  const [submitting, setSubmitting] = useState(false);

  const strip = (v: string) => (v || "").replace(/<[^>]*>/g, "");
  // 네이버는 안정 place id가 없어 이름|도로명주소를 placeKey로 사용
  const keyOf = (it: NaverItem) => `${strip(it.title)}|${strip(it.roadAddress || it.address)}`;

  const loadSummaries = async (list: NaverItem[]) => {
    const keys = list.map(keyOf);
    if (keys.length === 0) return;
    try {
      const res = (await executePromise(HospitalReviewApi.getSummaries("NAVER", keys))) as unknown as Array<{ placeKey: string; avgRating: number | null; reviewCount: number }>;
      const map: Record<string, HospSummary> = {};
      res.forEach((r) => { map[r.placeKey] = { avgRating: r.avgRating, reviewCount: r.reviewCount }; });
      setSummaries(map);
    } catch { setSummaries({}); }
  };

  const search = async (q: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setOpen(null);
    try {
      // 백엔드 프록시(네이버 지역검색). 배포 환경에서도 동작.
      const data = (await executePromise(HospitalReviewApi.search(q))) as unknown as { items?: NaverItem[] };
      const list: NaverItem[] = data?.items || [];
      setItems(list);
      loadSummaries(list);
    } catch { setItems([]); } finally { setLoading(false); }
  };

  useEffect(() => { search("소아정신과"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReviews = async (key: string) => {
    try {
      const res = (await executePromise(HospitalReviewApi.getReviews("NAVER", key))) as unknown as HospReview[];
      setReviews((p) => ({ ...p, [key]: res }));
    } catch { setReviews((p) => ({ ...p, [key]: [] })); }
  };

  const toggle = (key: string) => {
    const next = open === key ? null : key;
    setOpen(next);
    setForm({ rating: 5, content: "" });
    if (next) loadReviews(next);
  };

  const submitReview = async (it: NaverItem) => {
    if (!form.content.trim()) return;
    const key = keyOf(it);
    setSubmitting(true);
    try {
      await executePromise(HospitalReviewApi.createReview({
        provider: "NAVER", placeKey: key,
        hospitalName: strip(it.title), roadAddress: strip(it.roadAddress || it.address),
        rating: form.rating, content: form.content.trim(),
      }));
      setForm({ rating: 5, content: "" });
      await loadReviews(key);
      await loadSummaries(items); // 평점/리뷰수 갱신
    } catch (e) {
      alert("후기 등록 실패 (로그인/네트워크 확인)");
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input className={s.apiKeyInput} value={query} placeholder="예: 강남 소아정신과, 청소년발달센터"
          onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search(query)} />
        <button type="button" className={s.apiKeyBtn} onClick={() => search(query)}>검색</button>
      </div>
      {showFilters && (
        <div className={s.filterRow}>
          {HOSPITAL_FILTERS.map((f) => (
            <span key={f.key} className={s.fchip} onClick={() => { setQuery(f.label); search(f.label); }}>{f.label}</span>
          ))}
        </div>
      )}

      {/* 결과 영역: 검색 중에도 이전 결과 유지 + min-height로 높이 고정 → 스크롤 점프 방지 */}
      <div style={{ minHeight: 480 }}>
      {loading && <div style={{ padding: 12, color: "#B4B2A9" }}>검색 중…</div>}
      {!loading && items.length === 0 && <div style={{ padding: 20, color: "#B4B2A9" }}>결과가 없습니다.</div>}

      {items.map((it, i) => {
        const name = strip(it.title);
        const addr = strip(it.roadAddress || it.address);
        const key = keyOf(it);
        const sum = summaries[key];
        const homeUrl = it.link || `https://search.naver.com/search.naver?query=${encodeURIComponent(name)}`;
        const mapUrl = `https://map.naver.com/v5/search/${encodeURIComponent(name)}`;
        const isOpen = open === key;
        const list = reviews[key] || [];
        return (
          <div className={s.hospCard} key={i}>
            <div className={s.hospMain} onClick={() => toggle(key)}>
              <div className={s.hospInfo}>
                <div className={s.hospName}>{name}</div>
                <span className={`${s.hospType} ${s.htPsy}`}>{strip(it.category)}</span>
                <div className={s.hospAddr}>📍 {addr}</div>
                {it.telephone && <div className={s.hospAddr}>☎ {strip(it.telephone)}</div>}
                <div className={s.starRow}>
                  {sum && sum.reviewCount > 0 ? (
                    <>
                      <span className={s.stars}>{star(Math.round(sum.avgRating || 0))}</span>
                      <span className={s.sScore}>{(sum.avgRating || 0).toFixed(1)}</span>
                      <span className={s.sCnt}>({sum.reviewCount})</span>
                    </>
                  ) : (
                    <span className={s.sCnt}>후기 없음 · 첫 후기를 남겨보세요</span>
                  )}
                </div>
              </div>
            </div>
            {isOpen && (
              <div className={s.reviewPanel}>
                {/* AI 후기 요약: 기능 개발 예정 (Claude 연동 예정) */}
                <div className={s.aiSummary}>
                  <div className={s.aiLabel}>🤖 AI 후기 요약</div>
                  <div className={s.aiText} style={{ color: "#B4B2A9" }}>기능 개발 예정입니다.</div>
                </div>
                <div className={s.reviewTitle}>후기 {list.length}개</div>
                {list.map((r) => (
                  <div className={s.reviewItem} key={r.idx}>
                    <div className={s.reviewHeader}>
                      <span className={s.reviewStars}>{"★".repeat(r.rating)}</span>
                      <span style={{ fontSize: 11, color: "#5F5E5A" }}>{r.userName}</span>
                      <span className={s.reviewDate}>{fmtReviewDateTime(r.createAt)}</span>
                    </div>
                    <div className={s.reviewText}>{r.content}</div>
                  </div>
                ))}

                {/* 후기 작성 폼 */}
                <div className={s.reviewItem}>
                  <div className={s.reviewHeader}>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>후기 작성</span>
                    <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                      style={{ marginLeft: 8, padding: "2px 6px", borderRadius: 6 }}>
                      {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{"★".repeat(n)} {n}</option>)}
                    </select>
                  </div>
                  <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="이 병원 후기를 남겨주세요 (최대 1000자)" maxLength={1000}
                    style={{ width: "100%", minHeight: 60, padding: 8, border: "1px solid #ece9f5", borderRadius: 8, fontSize: 13, marginTop: 6, boxSizing: "border-box" }} />
                  <button type="button" className={s.hospBookBtn} disabled={submitting} onClick={() => submitReview(it)} style={{ marginTop: 6 }}>
                    {submitting ? "등록 중…" : "후기 등록"}
                  </button>
                </div>

                <div className={s.reviewActions}>
                  <a className={s.hospNaverBtn} href={homeUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "inline-block" }}>홈페이지</a>
                  <a className={s.hospKakaoBtn} href={mapUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", display: "inline-block" }}>지도보기</a>
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}

// ── 상담사 개별 리뷰 목록 (카드 펼침) ──
interface ConsultantReview { reviewStar: number | null; review: string | null; reviewedAt: string | null; reviewerName: string | null }
function ConsultantReviewList({ adminIdx }: { adminIdx: number }) {
  const [list, setList] = useState<ConsultantReview[] | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const res = (await executePromise(ConsultationApi.getConsultantReviews(adminIdx))) as unknown as ConsultantReview[];
        setList(res || []);
      } catch { setList([]); }
    })();
  }, [adminIdx]);

  return (
    <div className={s.consultantReviewBox}>
      {list === null ? (
        <div style={{ fontSize: 12, color: "#B4B2A9" }}>리뷰 불러오는 중…</div>
      ) : list.length === 0 ? (
        <div style={{ fontSize: 12, color: "#B4B2A9" }}>아직 작성된 상담 리뷰가 없습니다.</div>
      ) : list.map((r, i) => (
        <div key={i} className={s.reviewItem}>
          <div className={s.reviewHeader}>
            <span className={s.reviewStars}>{"★".repeat(r.reviewStar || 0)}</span>
            <span style={{ fontSize: 11, color: "#5F5E5A" }}>{r.reviewerName || "익명"}</span>
            <span className={s.reviewDate}>{fmtReviewDateTime(r.reviewedAt)}</span>
          </div>
          <div className={s.reviewText}>{r.review}</div>
        </div>
      ))}
    </div>
  );
}

// ── 상담사 예약 모달 (실제 상담신청 + 희망상담사 + 이용권 차감) ──
function ReservationModal({ target, profileIdx, contentExecutedInfoIdx, testLabel, onClose, onDone, notify }: {
  target: { adminIdx: number; name: string };
  profileIdx: number;
  contentExecutedInfoIdx: number;
  testLabel: string;
  onClose: () => void;
  onDone: () => void;
  notify: (desc: string) => void;
}) {
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState<{ idx: number; desiredTime: string; isReserved: boolean }[]>([]);
  const [reservationIdx, setReservationIdx] = useState<number | null>(null);
  const [inquiry, setInquiry] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 최소 신청일 = 오늘+3일 (기존 정책)
  const minDate = (() => { const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().slice(0, 10); })();

  const loadSlots = async (d: string) => {
    setLoadingSlots(true); setReservationIdx(null);
    try {
      const res = (await executePromise(ConsultationApi.getAvailableReservation({ desiredDate: d }))) as unknown as
        { idx: number; desiredTime: string; isReserved: boolean }[];
      setSlots(res || []);
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  };

  const submit = async () => {
    if (!date) return notify("희망 날짜를 선택해주세요.");
    if (reservationIdx == null) return notify("희망 시간을 선택해주세요.");
    if (inquiry.trim().length < 2) return notify("사전문의를 2자 이상 입력해주세요.");
    setSubmitting(true);
    try {
      await executePromise(ConsultationApi.applyCounseling({
        userProfileIdx: profileIdx,
        counselingReservationIdx: reservationIdx,
        contentExecutedInfoIdx,
        preQuestioning: inquiry.trim(),
        hopeConsultantAdminIdx: target.adminIdx,
      }));
      onDone();
    } catch (e: any) {
      // 백엔드 에러(이용권 없음/중복상담 등) 표면화
      let msg = "상담 신청에 실패했습니다.";
      try { const j = JSON.parse(e?.message || "{}"); if (j?.errorCode?.msg) msg = j.errorCode.msg; } catch {}
      notify(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className={s.modalBg} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={s.modal}>
        <div className={s.modalTitle}>{target.name} 상담 신청</div>
        <div className={s.modalSub}>{testLabel} · 신청 시 상담이용권 1매가 차감됩니다.</div>

        <div style={{ fontSize: 12, fontWeight: 600, margin: "6px 0 4px" }}>희망 날짜 (오늘+3일 이후)</div>
        <input type="date" className={s.modalSelect} min={minDate} value={date}
          onChange={(e) => { setDate(e.target.value); loadSlots(e.target.value); }} />

        {date && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, margin: "6px 0 4px" }}>희망 시간</div>
            {loadingSlots ? <div style={{ fontSize: 12, color: "#B4B2A9" }}>조회 중…</div> : (
              <div className={s.timeGrid}>
                {slots.length === 0 && <div style={{ fontSize: 12, color: "#B4B2A9", gridColumn: "1/4" }}>예약 가능한 시간이 없습니다.</div>}
                {slots.map((sl) => (
                  <div key={sl.idx}
                    className={`${s.tslot} ${sl.isReserved ? s.full : ""} ${reservationIdx === sl.idx ? s.sel : ""}`}
                    onClick={() => !sl.isReserved && setReservationIdx(sl.idx)}>
                    {sl.desiredTime?.slice(0, 5)}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div style={{ fontSize: 12, fontWeight: 600, margin: "6px 0 4px" }}>사전문의</div>
        <textarea value={inquiry} onChange={(e) => setInquiry(e.target.value)} maxLength={1000}
          placeholder="상담사에게 전할 사전문의를 입력해주세요 (2~1000자)"
          style={{ width: "100%", minHeight: 70, padding: 8, border: "1px solid #ece9f5", borderRadius: 8, fontSize: 13, boxSizing: "border-box", marginBottom: 12 }} />

        <div className={s.modalActions}>
          <button type="button" className={s.modalCancel} onClick={onClose}>취소</button>
          <button type="button" className={s.modalConfirm} disabled={submitting} onClick={submit}>{submitting ? "신청 중…" : "상담 신청"}</button>
        </div>
      </div>
    </div>
  );
}
